import React from 'react';
import {
  RADAR_DIMENSION_KEYS,
  RadarDimensionKey,
  RadarDimensionLabelVariant,
  getRadarDimensionLabel,
} from '../../utils/radarDimensions';

interface JobCandidateRadarProps {
  dimensionWeights: Record<string, number>;
  dimensionScores?: Record<string, number>;
  size?: number;
  showLabels?: boolean;
  labelVariant?: RadarDimensionLabelVariant;
  labelFontSize?: number;
  className?: string;
  jobFillColor?: string;
  jobStrokeColor?: string;
  jobStrokeDasharray?: string;
  candidateFillColor?: string;
  candidateStrokeColor?: string;
}

const clampPercent = (value: number): number => Math.max(0, Math.min(100, value));

const normalizeWeightsToScale = (weights: Record<string, number>): number[] => {
  const values = RADAR_DIMENSION_KEYS.map(key => Number(weights[key] ?? 0));
  const maxWeight = Math.max(...values, 0.001);
  return values.map(value => clampPercent((value / maxWeight) * 100));
};

const normalizeScores = (scores: Record<string, number>): number[] => {
  return RADAR_DIMENSION_KEYS.map(key => clampPercent(Number(scores[key] ?? 0)));
};

const pointFor = (cx: number, cy: number, radius: number, index: number, total: number, value: number) => {
  const angle = (-Math.PI / 2) + (2 * Math.PI * index) / total;
  const scale = value / 100;
  return {
    x: cx + Math.cos(angle) * radius * scale,
    y: cy + Math.sin(angle) * radius * scale,
  };
};

const pointsToPath = (points: Array<{ x: number; y: number }>): string => {
  if (points.length === 0) return '';
  const [first, ...rest] = points;
  return `M ${first.x} ${first.y} ${rest.map(p => `L ${p.x} ${p.y}`).join(' ')} Z`;
};

const JobCandidateRadar: React.FC<JobCandidateRadarProps> = ({
  dimensionWeights,
  dimensionScores,
  size = 220,
  showLabels = false,
  labelVariant = 'full',
  labelFontSize = 10,
  className,
  jobFillColor = 'rgba(59, 130, 246, 0.14)',
  jobStrokeColor = 'rgba(59, 130, 246, 0.55)',
  jobStrokeDasharray = '5 3',
  candidateFillColor = 'rgba(16, 185, 129, 0.22)',
  candidateStrokeColor = 'rgba(16, 185, 129, 0.92)',
}) => {
  const hasWeights = !!dimensionWeights && Object.keys(dimensionWeights).length > 0;
  const hasScores = !!dimensionScores && Object.keys(dimensionScores).length > 0;
  if (!hasWeights) return null;

  const padding = showLabels ? 36 : 18;
  const drawSize = Math.max(120, size);
  const innerSize = drawSize - (padding * 2);
  const radius = innerSize / 2;
  const center = { x: drawSize / 2, y: drawSize / 2 };

  const jdProfile = normalizeWeightsToScale(dimensionWeights);
  const candidateProfile = hasScores ? normalizeScores(dimensionScores) : [];

  const jdPoints = jdProfile.map((value, index) => pointFor(center.x, center.y, radius, index, RADAR_DIMENSION_KEYS.length, value));
  const candidatePoints = candidateProfile.map((value, index) => pointFor(center.x, center.y, radius, index, RADAR_DIMENSION_KEYS.length, value));

  const jdPath = pointsToPath(jdPoints);
  const candidatePath = pointsToPath(candidatePoints);
  const levels = [25, 50, 75, 100];

  return (
    <svg
      className={className}
      width={drawSize}
      height={drawSize}
      viewBox={`0 0 ${drawSize} ${drawSize}`}
      role="img"
      aria-label={hasScores ? 'Candidate fit versus job profile radar' : 'Job profile radar'}
    >
      {levels.map(level => (
        <polygon
          key={level}
          points={RADAR_DIMENSION_KEYS.map((_, index) => {
            const p = pointFor(center.x, center.y, radius, index, RADAR_DIMENSION_KEYS.length, level);
            return `${p.x},${p.y}`;
          }).join(' ')}
          fill="none"
          stroke="rgba(107, 114, 128, 0.16)"
          strokeWidth="1"
        />
      ))}

      {RADAR_DIMENSION_KEYS.map((_, index) => {
        const outer = pointFor(center.x, center.y, radius, index, RADAR_DIMENSION_KEYS.length, 100);
        return (
          <line
            key={index}
            x1={center.x}
            y1={center.y}
            x2={outer.x}
            y2={outer.y}
            stroke="rgba(107, 114, 128, 0.18)"
            strokeWidth="1"
          />
        );
      })}

      <path d={jdPath} fill={jobFillColor} stroke={jobStrokeColor} strokeWidth="1.5" strokeDasharray={jobStrokeDasharray} />
      {hasScores && (
        <>
          <path d={candidatePath} fill={candidateFillColor} stroke={candidateStrokeColor} strokeWidth="2" />
          {candidatePoints.map((point, index) => (
            <circle key={index} cx={point.x} cy={point.y} r="2.6" fill={candidateStrokeColor} />
          ))}
        </>
      )}

      {showLabels && RADAR_DIMENSION_KEYS.map((key: RadarDimensionKey, index) => {
        const point = pointFor(center.x, center.y, radius + 16, index, RADAR_DIMENSION_KEYS.length, 100);
        return (
          <text
            key={key}
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={labelFontSize}
            fill="var(--text-muted)"
          >
            {getRadarDimensionLabel(key, labelVariant)}
          </text>
        );
      })}
    </svg>
  );
};

export default JobCandidateRadar;
